/**
 * Configuración central del brand — lado frontend (Vite).
 *
 * Todos los valores se leen desde variables de entorno VITE_BRAND_*.
 * Configure las variables en Railway para personalizar el brand.
 *
 * Para adaptar este proyecto a otro brand (multi-creator SaaS):
 *   1. Cambiar las VITE_BRAND_* en Railway (producción) o en .env.local (local).
 *   2. NO es necesario tocar ningún componente React.
 *
 * Variables gemelas en el backend:
 *   - Frontend: src/lib/brandConfig.ts  (VITE_BRAND_* con prefijo VITE_)
 *   - Backend:  api/lib/brandConfig.ts  (BRAND_* sin prefijo)
 * Ambos deben compartir la misma convención de nombres.
 *
 * Scripts Node post-build (tsx) importan este módulo sin Vite — leen process.env
 * con fallback VITE_* → BRAND_* (mismo contrato que api/lib/brandConfig.ts).
 */

const _viteMetaEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env;

/** Vite bundle o process.env (Railway build / tsx scripts). */
const env: Record<string, string | undefined> = new Proxy(
  {},
  {
    get(_target, key: string) {
      const fromVite = _viteMetaEnv?.[key];
      if (fromVite) return fromVite;
      if (typeof process === "undefined") return undefined;
      const bare = key.startsWith("VITE_") ? key.slice(5) : key;
      return process.env[key] ?? process.env[bare];
    },
  },
);

// ── Identidad ────────────────────────────────────────────────────────────────

/**
 * Nombre legal / real de la persona creadora.
 * Usado en StructuredData JSON-LD, i18n, SEO, alt text.
 */
export const BRAND_PERSON_NAME =
  env.VITE_BRAND_PERSON_NAME ||
  env.VITE_BRAND_NAME ||
  "";

/**
 * Nombre público del brand / handle.
 * Puede ser diferente al nombre legal. Usado en footers, títulos, emails.
 * Fallback: BRAND_PERSON_NAME para que el footer nunca muestre vacío.
 */
export const BRAND_NAME =
  env.VITE_BRAND_NAME ||
  env.VITE_BRAND_PERSON_NAME ||
  "";

/**
 * Clave corta en minúsculas, sin espacios (ej. "micreadora").
 * Usada en:
 *   - Stripe/MP metadata `source` → identifica pagos del brand
 *   - `client_reference_id` en URLs de pago
 *   - Mux `creator_id` para filtrar videos
 *   - PostHog `site` property
 *   - DB: manual_transactions.source, site, brand
 * DEBE coincidir con BRAND_SOURCE_KEY en el backend.
 */
export const BRAND_SOURCE_KEY =
  env.VITE_BRAND_SOURCE_KEY ?? "juaperrone";

if (BRAND_SOURCE_KEY && BRAND_SOURCE_KEY !== "juaperrone") {
  console.error(
    `[brandConfig] VITE_BRAND_SOURCE_KEY="${BRAND_SOURCE_KEY}" — MUST be exactly "juaperrone" for this project. ` +
      "Wrong value will break Stripe metadata, payments attribution, admin filters, etc. Fix your .env / Vercel vars."
  );
}

/**
 * Handle en redes sociales (sin @).
 * Usado en StructuredData alternateName, SEO keywords.
 */
export const BRAND_HANDLE =
  env.VITE_BRAND_HANDLE ?? BRAND_SOURCE_KEY;

/**
 * Edad del creador para mostrar en bio y SEO.
 * Usada en descripciones, metadatos y textos del sitio.
 */
export const BRAND_AGE =
  env.VITE_BRAND_AGE ?? "";

/**
 * Ciudad/ubicación del creador para bio y SEO.
 * Ej. "Buenos Aires (CABA)", "Córdoba", "Madrid".
 * Configurable por brand — no hardcodear en componentes.
 */
export const BRAND_CITY =
  env.VITE_BRAND_CITY ?? "";

// ── URLs ─────────────────────────────────────────────────────────────────────

/**
 * URL del sitio principal (este proyecto). Ej. "https://micreadora.com".
 *
 * Default usa el apex (sin `www.`) para que canonical/OG meta no apunten al
 * subdominio `www.`, que típicamente tiene un 308 → apex y rompe CORS para
 * recursos (ej. og.png) al solicitarlos como crossorigin.
 */
export const BRAND_SITE_URL =
  env.VITE_BRAND_SITE_URL ??
  env.VITE_MAIN_SITE_URL ??
  (BRAND_SOURCE_KEY ? `https://${BRAND_SOURCE_KEY}.com` : "");

/**
 * Hostname corto del sitio principal, sin protocolo ni www.
 * Ej. "micreadora.com". Usado en footer links, siteGeoPolicy.
 */
export const BRAND_SITE_HOST = BRAND_SITE_URL
  ? BRAND_SITE_URL.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
  : "";

/** Primer string URL no vacío (tras trim); ignora "" para que `??` en cadena anterior no bloquee fallbacks. */
function firstNonEmptyUrl(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) return t;
  }
  return undefined;
}

/**
 * Base para enlaces de "membresía / subscripción" en el mismo sitio.
 *
 * Orden (Railway / .env):
 * 1. `VITE_FAN_SITE_URL` — build también puede inyectarla desde `FAN_SITE_URL` / `BRAND_FAN_URL` (ver `vite.config.ts`).
 * 2. `VITE_BRAND_FAN_URL`
 * 3. Default = **`BRAND_SITE_URL`** (mismo dominio que el sitio principal).
 */
export const BRAND_FAN_URL =
  firstNonEmptyUrl(
    env.VITE_FAN_SITE_URL,
    env.VITE_BRAND_FAN_URL,
  ) ?? BRAND_SITE_URL;

// ── Pagos ────────────────────────────────────────────────────────────────────

/**
 * `client_reference_id` que se añade a URLs de Stripe/MercadoPago.
 * Igual que BRAND_SOURCE_KEY — centralizado aquí para claridad.
 */
export const BRAND_CLIENT_REFERENCE_ID = BRAND_SOURCE_KEY;

// ── Mux ──────────────────────────────────────────────────────────────────────

/**
 * `creator_id` usado como filtro en la API de Mux Data.
 * Identifica los assets de este brand en una cuenta Mux compartida.
 */
export const BRAND_MUX_CREATOR_ID =
  env.VITE_BRAND_MUX_CREATOR_ID ?? BRAND_SOURCE_KEY;

// ── i18n / SEO ───────────────────────────────────────────────────────────────

/** Nombre del sitio para i18n, Open Graph, StructuredData. */
export const BRAND_SITE_NAME =
  env.VITE_BRAND_SITE_NAME ?? BRAND_PERSON_NAME;

// ── Emails (frontend-visible) ─────────────────────────────────────────────────
/** Email de soporte visible al usuario (ej. "hola@micreadora.com"). */
export const BRAND_SUPPORT_EMAIL =
  env.VITE_BRAND_SUPPORT_EMAIL ??
  env.VITE_SUPPORT_EMAIL ??
  (BRAND_SITE_HOST ? `hola@${BRAND_SITE_HOST}` : "");

// ── URLs de Subdomains ───────────────────────────────────────────────────────

/** URL del admin dashboard/menu (ej. "https://menu.micreadora.com"). */
export const BRAND_MENU_URL =
  env.VITE_BRAND_MENU_URL ??
  (BRAND_SOURCE_KEY ? `https://menu.${BRAND_SOURCE_KEY}.com` : "");

// ── Social Media ─────────────────────────────────────────────────────────────

/** Handle de Instagram sin @ (ej. "micreadora"). */
export const BRAND_INSTAGRAM_HANDLE =
  env.VITE_BRAND_INSTAGRAM_HANDLE ?? BRAND_HANDLE;

/** Handle de TikTok sin @ (ej. "micreadora"). */
export const BRAND_TIKTOK_HANDLE =
  env.VITE_BRAND_TIKTOK_HANDLE ?? BRAND_HANDLE;

/** Handle de Telegram sin @ (ej. "micreadora"). */
export const BRAND_TELEGRAM_HANDLE =
  env.VITE_BRAND_TELEGRAM_HANDLE ?? BRAND_HANDLE;

/** Handle de YouTube sin @ (ej. "micreadora"). */
export const BRAND_YOUTUBE_HANDLE =
  env.VITE_BRAND_YOUTUBE_HANDLE ?? BRAND_HANDLE;

/** URL completa de Instagram. */
export const BRAND_INSTAGRAM_URL =
  env.VITE_BRAND_INSTAGRAM_URL ??
  (BRAND_INSTAGRAM_HANDLE ? `https://www.instagram.com/${BRAND_INSTAGRAM_HANDLE}` : "");

/** URL completa de TikTok. */
export const BRAND_TIKTOK_URL =
  env.VITE_BRAND_TIKTOK_URL ??
  (BRAND_TIKTOK_HANDLE ? `https://www.tiktok.com/@${BRAND_TIKTOK_HANDLE}` : "");

/** URL completa de Telegram. */
export const BRAND_TELEGRAM_URL =
  env.VITE_BRAND_TELEGRAM_URL ??
  (BRAND_TELEGRAM_HANDLE ? `https://t.me/${BRAND_TELEGRAM_HANDLE}` : "");

/** URL completa de YouTube. */
export const BRAND_YOUTUBE_URL =
  env.VITE_BRAND_YOUTUBE_URL ??
  (BRAND_YOUTUBE_HANDLE ? `https://www.youtube.com/@${BRAND_YOUTUBE_HANDLE}` : "");

// ── CDN / Assets ─────────────────────────────────────────────────────────────

/**
 * Carpeta en la Media Library de Cloudinary para este creador (ej. "micreadora/portfolio").
 * `VITE_CLOUDINARY_CLOUD_NAME` es el id de la cuenta compartida (subdominio API), no una ruta de carpeta.
 */
export const BRAND_CLOUDINARY_FOLDER =
  env.VITE_BRAND_CLOUDINARY_FOLDER ??
  env.VITE_CLOUDINARY_BASE_FOLDER ??
  (BRAND_SOURCE_KEY ? `${BRAND_SOURCE_KEY}/portfolio` : "");

/** URL del banner/header image principal. */
export const BRAND_BANNER_IMAGE_URL =
  env.VITE_BRAND_BANNER_IMAGE_URL ??
  env.VITE_BANNER_IMAGE_URL ??
  "https://res.cloudinary.com/nm-creators/image/upload/f_auto,q_auto/v1779993658/header-banner-juaperrone_rxgxxd.png";

// ── Storage Keys (Frontend) ──────────────────────────────────────────────────

/** Prefijo para localStorage/sessionStorage keys (ej. "micreadora"). */
export const BRAND_STORAGE_PREFIX = BRAND_SOURCE_KEY;

// ── SEO & Metadata ───────────────────────────────────────────────────────────

/** Descripción del brand para meta tags y SEO. */
export const BRAND_DESCRIPTION =
  env.VITE_BRAND_DESCRIPTION ??
  "";

/** Locale principal (ej. "es_AR"). */
export const BRAND_LOCALE = env.VITE_BRAND_LOCALE ?? "es_AR";

/** Código de idioma (ej. "es"). */
export const BRAND_LANGUAGE = env.VITE_BRAND_LANGUAGE ?? "es";

/** País del brand (ej. "Argentina"). */
export const BRAND_COUNTRY = env.VITE_BRAND_COUNTRY ?? "Argentina";

/** URL de la imagen OG/Twitter. */
export const BRAND_OG_IMAGE_URL =
  env.VITE_BRAND_OG_IMAGE_URL ??
  "https://res.cloudinary.com/nm-creators/image/upload/v1779593366/juaperrone/og_juiol7.png";

/** Logo cuadrado del brand (Cloudinary juaperrone/, 512×512). Stripe Checkout + Clerk. */
const DEFAULT_BRAND_LOGO_URL =
  "https://res.cloudinary.com/nm-creators/image/upload/v1780226056/web-app-manifest-512x512_faxbik.png";

/** Logo HTTPS para UI embebida (p. ej. Clerk SignUp/SignIn). Cuadrado recomendado. */
export const BRAND_AUTH_LOGO_URL =
  env.VITE_BRAND_AUTH_LOGO_URL ??
  env.VITE_BRAND_LOGO_URL ??
  DEFAULT_BRAND_LOGO_URL;

/** Código de verificación de Google Search Console. */
export const BRAND_GSC_VERIFICATION =
  env.VITE_BRAND_GSC_VERIFICATION ??
  "";
